import * as React from 'react';
import { Currency, DiscountBandTypeEnum, DiscountValueType, DiscountBandType } from "app/shared/model";
import { EditableDiscountBand } from 'app/pages/DiscountEditor/modules/DiscountActions';
import { DiscreteBandEditor } from 'app/pages/DiscountEditor/components/EditDiscountActionDialog/DiscreteBandEditor';
import { CulmulativeBandEditor } from 'app/pages/DiscountEditor/components/EditDiscountActionDialog/CumulativeBandEditor';
import { Select, Popover, Typography, Alert } from 'antd';

const { Text } = Typography;

export interface BandsEditorProps {
    bandType: DiscountBandTypeEnum
    bands: Readonly<EditableDiscountBand>[]
    onUpdateBands: (bands: Readonly<EditableDiscountBand>[]) => void,
    changeValueType: (value: any) => void
    changeCurrency: (currencyCode: string) => void
    valueType: DiscountValueType
    showValidation: boolean
    readOnly: boolean
    allCurrencies: Currency[]
    currencyCode: string
    currencySymbol: string,
}

export const BandsEditor = (props: BandsEditorProps) => (
    <div>
        <BandsDescription {...props} />
        <div style={{ marginTop: 16, marginBottom: 24 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label style={{ fontWeight: 600, color: '#262626', fontSize: 14 }}>Discount type</label>
                    <Select
                        style={{ width: 200 }}
                        options={DiscountValueTypeOptions}
                        value={props.valueType}
                        onChange={(value) => props.changeValueType(value)}
                        disabled={props.readOnly}
                    />
                </div>
                {(props.valueType === DiscountValueType.AmountPerUnit ||
                    props.valueType === DiscountValueType.OverrideUnitPrice) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <label style={{ fontWeight: 600, color: '#262626', fontSize: 14 }}>Currency</label>
                        <Select
                            style={{ width: 100 }}
                            value={props.currencyCode || undefined}
                            onChange={(value) => props.changeCurrency(value)}
                            options={props.allCurrencies.map((c) => ({ label: c.code, value: c.code }))}
                            disabled={props.readOnly}
                            placeholder="Currency"
                        />
                    </div>
                )}
            </div>
        </div> 
        <BandsEditorBands {...props} />
    </div>
)

const BandsDescription = (props: BandsEditorProps) => {
    const type = DiscountBandType.FromEnum(props.bandType);
    const descriptionDetails = 
        <div>
            <h4>{type.Mode}</h4>
            <p>
                {type.Description}
            </p>
            <p>
                <strong>Example</strong><br />{type.Example}
            </p>
            <p>
                {type.ExampleExplanation}
            </p>
    </div>;

    return <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            This discount tiers will be applied in { ' ' }
            <Popover 
                content={descriptionDetails}
                trigger="hover">
                <span style={{textDecoration: 'underline dotted', cursor: 'help'}}>{type.Mode.toLowerCase()}</span>
            </Popover>
        </Text>
}

const DiscountValueTypeOptions = [
    {key: DiscountValueType.Percent, value: DiscountValueType.Percent, label: 'Percentage discount'},
    {key: DiscountValueType.AmountPerUnit, value: DiscountValueType.AmountPerUnit, label: 'Amount off per unit'},
    {key: DiscountValueType.OverrideUnitPrice, value: DiscountValueType.OverrideUnitPrice, label: 'Override the unit price' }
]

const BandsEditorBands = (props: BandsEditorProps) => {
    switch (props.bandType) {
        case DiscountBandTypeEnum.Compound:
            return <CulmulativeBandEditor
                    bands={props.bands}
                    onUpdateBands={props.onUpdateBands}
                    showValidation={props.showValidation}
                    discountValueType={props.valueType}
                    readOnly={props.readOnly}
                    currencySymbol={props.currencySymbol}
                />;

        case DiscountBandTypeEnum.Discrete:
            return <DiscreteBandEditor
                bands={props.bands}
                onUpdateBands={props.onUpdateBands}
                valueType={props.valueType}
                showValidation={props.showValidation}
                readOnly={props.readOnly}
                currencyCode={props.currencyCode}
                currencySymbol={props.currencySymbol}
            />

        default:
            return <Alert 
                type="error"
                title={`Unknown band type: ${props.bandType}`}
                showIcon
            />;
    }
}
